<template>
    <div class="flex flex-col gap-3 lg:grid lg:gap-3 lg:grid-cols-[min-content_min-content_auto]">
        <div
            v-for="displayDay in displayDays"
            :key="displayDay.day"
            class="flex flex-wrap gap-3 rounded-2xl border px-4 py-4 lg:py-2.5 min-h-[61px] transition lg:col-span-3 lg:grid lg:grid-cols-subgrid lg:items-start"
            :class="[displayDay.enabled ? 'bg-white shadow border-white' : 'bg-slate-100 border-slate-100']"
        >
            <div class="flex-none">
                <div class="pt-0.5 lg:pt-2.5">
                    <vx-switch
                        :modelValue="displayDay.enabled"
                        @update:modelValue="(value) => toggleDay(displayDay.day, value)"
                        :aria-label="displayDay.label"
                        size="sm"
                    />
                </div>
            </div>

            <div class="flex-1 font-medium text-sm pt-0.5 lg:flex-none">
                <div class="lg:pt-2.5">
                    {{ displayDay.label }}
                </div>
            </div>

            <div class="flex flex-col gap-3 w-full relative">
                <template v-if="displayDay.enabled">
                    <div
                        v-for="(timeBlock, blockIndex) in displayDay.blocks"
                        :key="`${displayDay.day}-${blockIndex}`"
                        class="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-2 w-full"
                    >
                        <div class="grow w-full">
                            <!-- @vue-ignore -->
                            <vx-selectable
                                :modelValue="timeBlock.start"
                                @update:modelValue="(value) => updateTime(displayDay.day, blockIndex, 'start', value)"
                                :reduce="(option) => option.value"
                                :clearable="false"
                                :options="timeOptions"
                                :selectable="(option) => option.value < timeBlock.end"
                                size="md"
                            />
                        </div>
                        <div class="grow w-full relative">
                            <div
                                class="absolute w-px h-2 lg:h-px lg:w-2 bg-slate-300 -inset-bs-2 inset-s-1/2 lg:inset-bs-1/2 lg:-inset-s-2"
                            ></div>
                            <!-- @vue-ignore -->
                            <vx-selectable
                                :modelValue="timeBlock.end"
                                @update:modelValue="(value) => updateTime(displayDay.day, blockIndex, 'end', value)"
                                :reduce="(option) => option.value"
                                :clearable="false"
                                :options="timeOptions"
                                :selectable="(option) => option.value > timeBlock.start"
                                size="md"
                            />
                        </div>

                        <div class="flex gap-2 w-full lg:w-auto">
                            <vx-button
                                v-if="displayDay.blocks.length > 1"
                                type="button"
                                color="muted-light"
                                size="md"
                                @click="removeTimeBlock(displayDay.day, blockIndex)"
                                class="w-[45px] shrink-0"
                                aria-label="Remove time slot"
                            >
                                <font-awesome-icon widthAuto :icon="faTrash" />
                            </vx-button>
                            <vx-button
                                v-else
                                type="button"
                                color="muted-light"
                                size="md"
                                @click="resetTimeBlock(displayDay.day, blockIndex)"
                                :disabled="isDefaultTime(timeBlock.start, timeBlock.end)"
                                class="w-[45px] shrink-0"
                                aria-label="Reset time slot"
                            >
                                <font-awesome-icon widthAuto :icon="faArrowRotateLeft" />
                            </vx-button>

                            <vx-button
                                type="button"
                                color="muted-light"
                                size="md"
                                :disabled="!canAddMoreBlocks()"
                                @click="addTimeBlock(displayDay.day, blockIndex)"
                                class="grow lg:w-[45px]"
                                aria-label="Add time slot"
                            >
                                <font-awesome-icon widthAuto :icon="faPlus" />
                            </vx-button>
                        </div>
                    </div>
                </template>
                <div
                    class="absolute inset-bs-0 inset-be-0 bg-slate-100 text-sm inset-s-0 w-full pointer-events-none transition lg:pt-3"
                    :class="{ 'opacity-0': displayDay.enabled }"
                >
                    No sending allowed
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPlus, faTrash, faArrowRotateLeft } from '@fortawesome/pro-solid-svg-icons';
import { VxSelectable, VxSwitch, VxButton } from '@voxie/frontend-components';
import { useToasted } from '~/composables/useToasted';

interface TimeRange {
    day: string;
    start: number;
    end: number;
}

interface TimeBlock {
    start: number;
    end: number;
}

interface DisplayDay {
    day: string;
    label: string;
    enabled: boolean;
    blocks: TimeBlock[];
}

const toasted = useToasted();
const dayOptions = [
    { label: 'Monday', value: 'mon' },
    { label: 'Tuesday', value: 'tue' },
    { label: 'Wednesday', value: 'wed' },
    { label: 'Thursday', value: 'thu' },
    { label: 'Friday', value: 'fri' },
    { label: 'Saturday', value: 'sat' },
    { label: 'Sunday', value: 'sun' },
];

const timeOptions = [
    { label: '8:00 am', value: 8 },
    { label: '9:00 am', value: 9 },
    { label: '10:00 am', value: 10 },
    { label: '11:00 am', value: 11 },
    { label: '12:00 pm', value: 12 },
    { label: '1:00 pm', value: 13 },
    { label: '2:00 pm', value: 14 },
    { label: '3:00 pm', value: 15 },
    { label: '4:00 pm', value: 16 },
    { label: '5:00 pm', value: 17 },
    { label: '6:00 pm', value: 18 },
    { label: '7:00 pm', value: 19 },
    { label: '8:00 pm', value: 20 },
];

const MAX_TIME_BLOCKS = 20;
const DEFAULT_START = 9;
const DEFAULT_END = 18;

const timeRanges = defineModel<TimeRange[]>({
    default: () => [],
});

const displayDays = computed((): DisplayDay[] => {
    return dayOptions.map((dayOption) => {
        const blocksForDay = timeRanges.value.filter((range) => range.day === dayOption.value);
        return {
            day: dayOption.value,
            label: dayOption.label,
            enabled: blocksForDay.length > 0,
            blocks: blocksForDay.map((block) => ({
                start: block.start,
                end: block.end,
            })),
        };
    });
});

const getTimeBlocksForDay = (day: string): TimeRange[] => {
    return timeRanges.value.filter((range) => range.day === day);
};

const isDefaultTime = (start: number, end: number): boolean => {
    return start === DEFAULT_START && end === DEFAULT_END;
};

const canAddMoreBlocks = (): boolean => {
    return timeRanges.value.length < MAX_TIME_BLOCKS;
};

const toggleDay = (day: string, enabled: boolean) => {
    if (!enabled) {
        timeRanges.value = timeRanges.value.filter((range) => range.day !== day);
        return;
    }

    if (!canAddMoreBlocks()) {
        toasted.error(`You can only add ${MAX_TIME_BLOCKS} time slots`);
        return;
    }

    timeRanges.value.push({
        day,
        start: DEFAULT_START,
        end: DEFAULT_END,
    });
};

const updateTime = (day: string, blockIndex: number, field: 'start' | 'end', value: number) => {
    const blocksForDay = getTimeBlocksForDay(day);
    const blockToUpdate = blocksForDay[blockIndex];
    if (!blockToUpdate) {
        return;
    }

    const modelIndex = timeRanges.value.indexOf(blockToUpdate);
    if (modelIndex === -1) {
        return;
    }

    const block = timeRanges.value[modelIndex];
    if (!block) {
        return;
    }

    block[field] = value;
};

const addTimeBlock = (day: string, afterBlockIndex?: number) => {
    if (!canAddMoreBlocks()) {
        return;
    }

    const blocksForDay = getTimeBlocksForDay(day);
    if (afterBlockIndex !== undefined && afterBlockIndex < blocksForDay.length) {
        const blockAfter = blocksForDay[afterBlockIndex];
        if (!blockAfter) {
            return;
        }

        const insertPosition = timeRanges.value.indexOf(blockAfter);
        if (insertPosition !== -1) {
            timeRanges.value.splice(insertPosition + 1, 0, {
                day,
                start: DEFAULT_START,
                end: DEFAULT_END,
            });
        }
    } else {
        timeRanges.value.push({
            day,
            start: DEFAULT_START,
            end: DEFAULT_END,
        });
    }
};

const removeTimeBlock = (day: string, blockIndex: number) => {
    const blocksForDay = getTimeBlocksForDay(day);
    const blockToRemove = blocksForDay[blockIndex];
    if (!blockToRemove) {
        return;
    }

    const removePosition = timeRanges.value.indexOf(blockToRemove);
    if (removePosition !== -1) {
        timeRanges.value.splice(removePosition, 1);
    }
};

const resetTimeBlock = (day: string, blockIndex: number) => {
    const blocksForDay = getTimeBlocksForDay(day);
    const blockToReset = blocksForDay[blockIndex];
    if (!blockToReset) {
        return;
    }

    const modelIndex = timeRanges.value.indexOf(blockToReset);
    if (modelIndex === -1) {
        return;
    }

    const block = timeRanges.value[modelIndex];
    if (!block) {
        return;
    }

    block.start = DEFAULT_START;
    block.end = DEFAULT_END;
};
</script>
